import type { EdgeFunctionCountryLookupResponse } from "@babybjorn/netlify-edge-functions";
import { addBreadcrumb } from "@sentry/gatsby";
import type { SetRequired } from "type-fest";
import { fetchEdgeFunction } from "../../utils/edge-functions";
import { isResponse } from "../type-guards/general";

const isCountryLookupResponse = (
	json: unknown,
): json is SetRequired<
	NonNullable<EdgeFunctionCountryLookupResponse>,
	"code"
> => {
	return typeof json === "object" && json !== null && "code" in json;
};

export const getUserLocation = async (): Promise<string | undefined> => {
	try {
		// Create abort signal with timeout, with fallback for older browsers
		const abortSignal =
			"timeout" in AbortSignal
				? AbortSignal.timeout(2000)
				: (() => {
						const controller = new AbortController();
						setTimeout(() => controller.abort(), 2000);
						return controller.signal;
					})();

		const response = await fetchEdgeFunction("/country-lookup", {
			signal: abortSignal,
		});

		if (!isResponse(response)) {
			throw new Error("Invalid response from country lookup");
		}

		const json = (await response.json()) as unknown;
		if (!isCountryLookupResponse(json)) {
			throw new Error("Invalid response from country lookup");
		}

		return json.code;
	} catch (error) {
		console.error(error);

		// Add breadcrumb to Sentry, but do not throw error.
		addBreadcrumb({
			level: "error",
			message: "Failed to get user location from external service",
			data: {
				error: JSON.stringify(error),
			},
		});

		return undefined;
	}
};
